import { useEffect, useId, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { Icon, iconFor } from "./icons.js";
import {
  activeGroup,
  countText,
  hasCount,
  type NavEntry,
  type NavGroup,
  type NavItem,
  nextIndex,
  visibleEntries,
} from "./nav-model.js";

// The sidebar's links and grouped menus (design v0.6, C3; moved from the top
// bar into the sidebar by design v0.9, V4). The group holding the current page
// lists its items under its heading. Any other group opens as a menu on click,
// not hover, in place (or beside a folded rail), and closes on Esc, a click
// outside or a choice. Keyboard: Tab to it, Enter, Space or Down opens it, the
// arrows, Home and End move, Enter chooses. Below 1024px the sidebar gives way
// to a top bar with one "Menu" button, which opens the same groups as sections.

function Count({ item }: { item: NavItem }) {
  if (!item.count) return null;
  return (
    <span className="nav-count" aria-label={`${item.count} waiting`}>
      {countText(item.count)}
    </span>
  );
}

function Menu({
  group,
  active,
  align = "left",
}: {
  group: NavGroup;
  active: boolean;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  // The current group stays listed under its heading, unless folded away.
  const [folded, setFolded] = useState(false);
  const pinned = active && !folded;
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLUListElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();

  const items = () =>
    Array.from(panel.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? []);
  const focusItem = (i: number) => items()[i]?.focus();
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  // A click anywhere else closes it.
  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);

  return (
    <div className={`nav-menu ${align}`} ref={wrap}>
      <button
        ref={button}
        type="button"
        className={`nav-menu-button${active ? " active" : ""}${open ? " open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open || pinned}
        aria-controls={id}
        onClick={() => {
          if (active) {
            setFolded(!folded);
            setOpen(false);
          } else setOpen(!open);
        }}
        onKeyDown={(e) => {
          if (pinned) return;
          if (e.key === "ArrowDown" || ((e.key === "Enter" || e.key === " ") && !open)) {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => focusItem(0));
          } else if (e.key === "Escape") {
            close(false);
          }
        }}
      >
        <Icon name={group.id} />
        <span className="rail-label">{group.label}</span>
        {hasCount(group) && !pinned ? (
          <span className="nav-dot" aria-label="has items waiting" />
        ) : null}
        <span className="chevron" aria-hidden="true">
          {open || pinned ? "▴" : "▾"}
        </span>
      </button>
      {pinned ? (
        <ul id={id} className="rail-items" aria-label={group.label}>
          {group.items.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} className="rail-link">
                <span className="rail-label">{item.label}</span>
                <Count item={item} />
              </NavLink>
            </li>
          ))}
        </ul>
      ) : open ? (
        <ul
          id={id}
          ref={panel}
          role="menu"
          aria-label={group.label}
          className="nav-panel"
          onKeyDown={(e) => {
            const list = items();
            const at = list.indexOf(document.activeElement as HTMLElement);
            if (e.key === "Escape") {
              e.preventDefault();
              close(true);
            } else if (e.key === "Tab") {
              setOpen(false);
            } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
              e.preventDefault();
              focusItem(nextIndex(at, e.key, list.length));
            }
          }}
        >
          {group.items.map((item) => (
            <li key={item.to} role="none">
              <NavLink role="menuitem" to={item.to} onClick={() => setOpen(false)}>
                <span className="nav-item-label">
                  {item.label}
                  <Count item={item} />
                </span>
                {item.description ? (
                  <span className="nav-item-description">{item.description}</span>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// The entries in order, for the sidebar. `align` right is the Admin menu at
// the sidebar's foot, whose menu opens upward.
export function NavEntries({
  entries,
  align = "left",
}: {
  entries: NavEntry[];
  align?: "left" | "right";
}) {
  const { pathname } = useLocation();
  const shown = visibleEntries(entries);
  const groups = shown.flatMap((e) => (e.kind === "menu" ? [e.group] : []));
  const current = activeGroup(groups, pathname);
  return (
    <span className="nav-wide">
      {shown.map((e) =>
        e.kind === "link" ? (
          <NavLink key={e.item.to} to={e.item.to} className="nav-link">
            <Icon name={iconFor(e.item.to)} />
            <span className="rail-label">{e.item.label}</span>
            <Count item={e.item} />
          </NavLink>
        ) : (
          <Menu key={e.group.id} group={e.group} active={current === e.group.id} align={align} />
        ),
      )}
    </span>
  );
}

// Below 1024px: one button that opens every group as a section.
export function NarrowMenu({ entries }: { entries: NavEntry[] }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const id = useId();
  // Choosing a page closes it.
  useEffect(() => setOpen(false), [pathname]);
  const shown = visibleEntries(entries);
  return (
    <div className="nav-narrow">
      <button
        type="button"
        className="nav-menu-button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      >
        Menu
        {shown.some((e) => (e.kind === "menu" ? hasCount(e.group) : (e.item.count ?? 0) > 0)) ? (
          <span className="nav-dot" aria-label="has items waiting" />
        ) : null}
        <span className="chevron" aria-hidden="true">
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open ? (
        <nav id={id} className="nav-sheet" aria-label="Menu">
          {shown.map((e) =>
            e.kind === "link" ? (
              <NavLink key={e.item.to} to={e.item.to} className="nav-sheet-link">
                {e.item.label}
                <Count item={e.item} />
              </NavLink>
            ) : (
              <section key={e.group.id}>
                <h2>{e.group.label}</h2>
                {e.group.items.map((i) => (
                  <NavLink key={i.to} to={i.to} className="nav-sheet-link">
                    {i.label}
                    <Count item={i} />
                  </NavLink>
                ))}
              </section>
            ),
          )}
        </nav>
      ) : null}
    </div>
  );
}
